import React, { useState, useEffect, useCallback } from 'react';
import { array, arrayOf, bool, func, object, oneOf, shape, string } from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';
import omit from 'lodash/omit';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import { useIntl, intlShape, FormattedMessage } from '../../util/reactIntl';
import {
  isAnyFilterActive,
  isMainSearchTypeKeywords,
  isOriginInUse,
  getQueryParamNames,
} from '../../util/search';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
  parse,
} from '../../util/urlHelpers';
import { createResourceLocatorString, pathByRouteName } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';

import { H3, H5, ModalInMobile, NamedRedirect, Page } from '../../components';
import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';

import { setActiveListing } from './SearchPage.duck';
import {
  groupListingFieldConfigs,
  initialValues,
  searchParamsPicker,
  validUrlQueryParamsFromProps,
  validFilterParams,
  cleanSearchFromConflictingParams,
  createSearchResultSchema,
  pickListingFieldFilters,
  omitLimitedListingFieldParams,
} from './SearchPage.shared';

import FilterComponent from './FilterComponent';
import SearchMap from './SearchMap/SearchMap';
import MainPanelHeader from './MainPanelHeader/MainPanelHeader';
import SearchFiltersSecondary from './SearchFiltersSecondary/SearchFiltersSecondary';
import SearchFiltersPrimary from './SearchFiltersPrimary/SearchFiltersPrimary';
import SearchFiltersMobile from './SearchFiltersMobile/SearchFiltersMobile';
import SortBy from './SortBy/SortBy';
import SearchResultsPanel from './SearchResultsPanel/SearchResultsPanel';
import NoSearchResultsMaybe from './NoSearchResultsMaybe/NoSearchResultsMaybe';

import css from './SearchPage.module.css';
import FooterComponent from '../FooterContainer/FooterContainer';

const MODAL_BREAKPOINT = 768;
const SEARCH_WITH_MAP_DEBOUNCE = 300;
const FILTER_DROPDOWN_OFFSET = -14;

const SearchPageComponent = props => {
  const {
    intl,
    listings,
    location,
    onManageDisableScrolling,
    pagination,
    scrollingDisabled,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
    onActivateListing,
    routeConfiguration,
    config,
  } = props;

  const history = useHistory();
  const [isSearchMapOpenOnMobile, setIsSearchMapOpenOnMobile] = useState(false);
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [currentQueryParams, setCurrentQueryParams] = useState(validUrlQueryParamsFromProps(props));
  const [isSecondaryFiltersOpen, setIsSecondaryFiltersOpen] = useState(false);

  const onMapMoveEnd = useCallback(
    debounce((viewportBoundsChanged, data) => {
      const { viewportBounds, viewportCenter } = data;

      const routes = routeConfiguration;
      const searchPagePath = pathByRouteName('SearchPage', routes);
      const currentPath =
        typeof window !== 'undefined' && window.location && window.location.pathname;

      const isSearchPage = currentPath === searchPagePath;

      if (viewportBoundsChanged && isSearchPage) {
        const { address, bounds, mapSearch, ...rest } = parse(location.search, {
          latlng: ['origin'],
          latlngBounds: ['bounds'],
        });

        const originMaybe = isOriginInUse(config) ? { origin: viewportCenter } : {};
        const dropNonFilterParams = false;

        const { listingFields: listingFieldsConfig } = config?.listing || {};
        const { defaultFilters: defaultFiltersConfig } = config?.search || {};
        const listingCategories = config.categoryConfiguration.categories;
        const filterConfigs = {
          listingFieldsConfig,
          defaultFiltersConfig,
          listingCategories,
        };

        const searchParamsLocal = {
          address,
          ...originMaybe,
          bounds: viewportBounds,
          mapSearch: true,
          ...validFilterParams(rest, filterConfigs, dropNonFilterParams),
        };

        history.push(createResourceLocatorString('SearchPage', routes, {}, searchParamsLocal));
      }
    }, SEARCH_WITH_MAP_DEBOUNCE),
    [history, routeConfiguration, config, location.search]
  );

  const onOpenMobileModal = () => setIsMobileModalOpen(true);
  const onCloseMobileModal = () => setIsMobileModalOpen(false);

  const applyFilters = () => {
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
    const listingCategories = config.categoryConfiguration.categories;
    const filterConfigs = {
      listingFieldsConfig,
      defaultFiltersConfig,
      listingCategories,
    };

    const urlQueryParams = validUrlQueryParamsFromProps(props);
    const searchParamsLocal = { ...urlQueryParams, ...currentQueryParams };
    const search = cleanSearchFromConflictingParams(searchParamsLocal, filterConfigs, sortConfig);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
  };

  const cancelFilters = () => {
    setCurrentQueryParams({});
  };

  const resetAll = e => {
    const { listingFields: listingFieldsConfig } = config?.listing || {};
    const { defaultFilters: defaultFiltersConfig } = config?.search || {};

    const urlQueryParams = validUrlQueryParamsFromProps(props);
    const filterQueryParamNames = getQueryParamNames(listingFieldsConfig, defaultFiltersConfig);

    setCurrentQueryParams({});

    const queryParams = omit(urlQueryParams, filterQueryParamNames);
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  };

  const getHandleChangedValueFn = useCallback(
    useHistoryPush => {
      const { listingFields: listingFieldsConfig } = config?.listing || {};
      const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};
      const listingCategories = config.categoryConfiguration.categories;
      const filterConfigs = {
        listingFieldsConfig,
        defaultFiltersConfig,
        listingCategories,
      };

      const urlQueryParams = validUrlQueryParamsFromProps(props);
      const keywordsMaybe = isMainSearchTypeKeywords(config)
        ? { keywords: urlQueryParams.keywords }
        : {};

      return updatedURLParams => {
        setCurrentQueryParams(prevCurrentQueryParams => {
          const mergedQueryParams = { ...urlQueryParams, ...prevCurrentQueryParams };

          const newCurrentQueryParams = omitLimitedListingFieldParams(
            {
              ...mergedQueryParams,
              ...updatedURLParams,
              ...keywordsMaybe,
              address: urlQueryParams.address,
              bounds: urlQueryParams.bounds,
            },
            filterConfigs
          );

          if (useHistoryPush) {
            const search = cleanSearchFromConflictingParams(
              newCurrentQueryParams,
              filterConfigs,
              sortConfig
            );
            history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, search));
          }

          return newCurrentQueryParams;
        });
      };
    },
    [config, props, history, routeConfiguration]
  );

  const handleSortBy = (urlParam, values) => {
    const urlQueryParams = validUrlQueryParamsFromProps(props);

    const queryParams = values
      ? { ...urlQueryParams, [urlParam]: values }
      : omit(urlQueryParams, urlParam);

    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, queryParams));
  };

  // Expand bounds automatically when a location search is performed (no mapSearch param)
  // so the displayed map area is larger (minimum ~0.5° in each direction).
  useEffect(() => {
    const searchParamsInURL = parse(location.search, {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    });

    if (!searchParamsInURL.mapSearch && searchParamsInURL.bounds) {
      const bounds = searchParamsInURL.bounds;
      const neLat = bounds.ne.lat;
      const neLng = bounds.ne.lng;
      const swLat = bounds.sw.lat;
      const swLng = bounds.sw.lng;

      const centerLat = (neLat + swLat) / 2;
      const centerLng = (neLng + swLng) / 2;
      const latDelta = neLat - swLat;
      const lngDelta = Math.abs(neLng - swLng);

      // Default search radius for a typed zip/town. Google returns a viewport
      // sized to the place itself (tiny for small towns), which can show an empty
      // map. We enforce a minimum span so sparse inventory still shows up.
      // 2.0 deg ~= 138 mi across (~69 mi half-radius). Widen further while
      // inventory is thin; tighten as it grows.
      const minLatDelta = 2.0;
      const minLngDelta = 2.0;

      let newNeLat = neLat;
      let newSwLat = swLat;
      let newNeLng = neLng;
      let newSwLng = swLng;

      if (latDelta < minLatDelta) {
        newNeLat = centerLat + minLatDelta / 2;
        newSwLat = centerLat - minLatDelta / 2;
      }

      if (lngDelta < minLngDelta) {
        newNeLng = centerLng + minLngDelta / 2;
        newSwLng = centerLng - minLngDelta / 2;
      }

      if (latDelta < minLatDelta || lngDelta < minLngDelta) {
        const newBoundsStr = `${newNeLat},${newNeLng},${newSwLat},${newSwLng}`;
        const newSearchParams = {
          ...searchParamsInURL,
          bounds: newBoundsStr,
          mapSearch: true,
        };
        history.push(
          createResourceLocatorString('SearchPage', routeConfiguration, {}, newSearchParams)
        );
      }
    }
  }, [location.search, history, routeConfiguration]);

  const { listingFields } = config?.listing || {};
  const { defaultFilters: defaultFiltersConfig, sortConfig } = config?.search || {};

  const activeListingTypes = config?.listing?.listingTypes.map(config => config.listingType);
  const marketplaceCurrency = config.currency;
  const categoryConfiguration = config.categoryConfiguration;
  const listingCategories = categoryConfiguration.categories;
  const listingFieldsConfig = pickListingFieldFilters({
    listingFields,
    locationSearch: location.search,
    categoryConfiguration,
  });
  const filterConfigs = {
    listingFieldsConfig,
    defaultFiltersConfig,
    listingCategories,
  };

  const { searchParamsAreInSync, urlQueryParams, searchParamsInURL } = searchParamsPicker(
    location.search,
    searchParams,
    filterConfigs,
    sortConfig,
    isOriginInUse(config)
  );

  const validQueryParams = urlQueryParams;

  const isWindowDefined = typeof window !== 'undefined';
  const isMobileLayout = isWindowDefined && window.innerWidth < MODAL_BREAKPOINT;
  const shouldShowSearchMap = !isMobileLayout || (isMobileLayout && isSearchMapOpenOnMobile);

  const isKeywordSearch = isMainSearchTypeKeywords(config);
  const builtInPrimaryFilters = defaultFiltersConfig.filter(f => ['categoryLevel'].includes(f.key));
  const builtInFilters = isKeywordSearch
    ? defaultFiltersConfig.filter(f => !['keywords', 'categoryLevel'].includes(f.key))
    : defaultFiltersConfig.filter(f => !['categoryLevel'].includes(f.key));
  const [customPrimaryFilters, customSecondaryFilters] = groupListingFieldConfigs(
    listingFieldsConfig,
    activeListingTypes
  );
  const availablePrimaryFilters = [
    ...builtInPrimaryFilters,
    ...customPrimaryFilters,
    ...builtInFilters,
  ];
  const availableFilters = [
    ...builtInPrimaryFilters,
    ...customPrimaryFilters,
    ...builtInFilters,
    ...customSecondaryFilters,
  ];

  const hasSecondaryFilters = !!(customSecondaryFilters && customSecondaryFilters.length > 0);

  const selectedFilters = validQueryParams;
  const keysOfSelectedFilters = Object.keys(selectedFilters);
  const selectedFiltersCountForMobile = isKeywordSearch
    ? keysOfSelectedFilters.filter(f => f !== 'keywords').length
    : keysOfSelectedFilters.length;
  const isValidDatesFilter =
    searchParamsInURL.dates == null ||
    (searchParamsInURL.dates != null && searchParamsInURL.dates === selectedFilters.dates);

  const selectedSecondaryFilters = hasSecondaryFilters
    ? validFilterParams(validQueryParams, {
        listingFieldsConfig: customSecondaryFilters,
        defaultFiltersConfig: [],
        listingCategories,
      })
    : {};
  const selectedSecondaryFiltersCount = Object.keys(selectedSecondaryFilters).length;

  const propsForSecondaryFiltersToggle = hasSecondaryFilters
    ? {
        isSecondaryFiltersOpen,
        toggleSecondaryFiltersOpen: isOpen => {
          setIsSecondaryFiltersOpen(isOpen);
          setCurrentQueryParams({});
        },
        selectedSecondaryFiltersCount,
      }
    : {};

  const hasPaginationInfo = !!pagination && pagination.totalItems != null;
  const totalItems =
    searchParamsAreInSync && hasPaginationInfo
      ? pagination.totalItems
      : pagination?.paginationUnsupported
      ? listings.length
      : 0;
  const listingsAreLoaded =
    !searchInProgress &&
    searchParamsAreInSync &&
    !!(hasPaginationInfo || pagination?.paginationUnsupported);

  const conflictingFilterActive = isAnyFilterActive(
    sortConfig.conflictingFilters,
    validQueryParams,
    filterConfigs
  );
  const sortBy = mode => {
    return sortConfig.active ? (
      <SortBy
        sort={validQueryParams[sortConfig.queryParamName]}
        isConflictingFilterActive={!!conflictingFilterActive}
        hasConflictingFilters={!!(sortConfig.conflictingFilters?.length > 0)}
        selectedFilters={selectedFilters}
        onSelect={handleSortBy}
        showAsPopup
        mode={mode}
        contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
      />
    ) : null;
  };
  const noResultsInfo = (
    <NoSearchResultsMaybe
      listingsAreLoaded={listingsAreLoaded}
      totalItems={totalItems}
      location={location}
      resetAll={resetAll}
    />
  );

  const { bounds, origin } = searchParamsInURL || {};

  const { title, description, schema } = createSearchResultSchema(
    listings,
    searchParamsInURL || {},
    intl,
    routeConfiguration,
    config
  );

  const topbarClasses = isMobileModalOpen
    ? classNames(css.topbarBehindModal, css.topbar)
    : css.topbar;

  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      description={description}
      title={title}
      schema={schema}
    >
      <TopbarContainer rootClassName={topbarClasses} currentSearchParams={validQueryParams} />
      <div className={css.container}>
        <div className={css.searchResultContainer}>
          <SearchFiltersMobile
            className={css.searchFiltersMobileMap}
            urlQueryParams={validQueryParams}
            sortByComponent={sortBy('mobile')}
            listingsAreLoaded={listingsAreLoaded}
            resultsCount={totalItems}
            searchInProgress={searchInProgress}
            searchListingsError={searchListingsError}
            showAsModalMaxWidth={MODAL_BREAKPOINT}
            onMapIconClick={() => setIsSearchMapOpenOnMobile(true)}
            onManageDisableScrolling={onManageDisableScrolling}
            onOpenModal={onOpenMobileModal}
            onCloseModal={onCloseMobileModal}
            resetAll={resetAll}
            selectedFiltersCount={selectedFiltersCountForMobile}
            noResultsInfo={noResultsInfo}
            isMapVariant
          >
            {availableFilters.map(filterConfig => {
              const key = `SearchFiltersMobile.${filterConfig.scope || 'built-in'}.${
                filterConfig.key
              }`;
              return (
                <FilterComponent
                  key={key}
                  idPrefix="SearchFiltersMobile"
                  config={filterConfig}
                  listingCategories={listingCategories}
                  marketplaceCurrency={marketplaceCurrency}
                  urlQueryParams={validQueryParams}
                  initialValues={initialValues(props, currentQueryParams)}
                  getHandleChangedValueFn={getHandleChangedValueFn}
                  intl={intl}
                  liveEdit
                  showAsPopup={false}
                />
              );
            })}
          </SearchFiltersMobile>
          <MainPanelHeader
            className={css.mainPanelMapVariant}
            sortByComponent={sortBy('desktop')}
            isSortByActive={sortConfig.active}
            listingsAreLoaded={listingsAreLoaded}
            resultsCount={totalItems}
            searchInProgress={searchInProgress}
            searchListingsError={searchListingsError}
            noResultsInfo={noResultsInfo}
          >
            <SearchFiltersPrimary {...propsForSecondaryFiltersToggle}>
              {availablePrimaryFilters.map(filterConfig => {
                const key = `SearchFiltersPrimary.${filterConfig.scope || 'built-in'}.${
                  filterConfig.key
                }`;
                return (
                  <FilterComponent
                    key={key}
                    idPrefix="SearchFiltersPrimary"
                    config={filterConfig}
                    listingCategories={listingCategories}
                    marketplaceCurrency={marketplaceCurrency}
                    urlQueryParams={validQueryParams}
                    initialValues={initialValues(props, currentQueryParams)}
                    getHandleChangedValueFn={getHandleChangedValueFn}
                    intl={intl}
                    showAsPopup
                    contentPlacementOffset={FILTER_DROPDOWN_OFFSET}
                  />
                );
              })}
            </SearchFiltersPrimary>
          </MainPanelHeader>
          {isSecondaryFiltersOpen ? (
            <div className={classNames(css.searchFiltersPanel)}>
              <SearchFiltersSecondary
                urlQueryParams={validQueryParams}
                listingsAreLoaded={listingsAreLoaded}
                applyFilters={applyFilters}
                cancelFilters={cancelFilters}
                resetAll={resetAll}
                onClosePanel={() => setIsSecondaryFiltersOpen(false)}
              >
                {customSecondaryFilters.map(filterConfig => {
                  const key = `SearchFiltersSecondary.${filterConfig.scope || 'built-in'}.${
                    filterConfig.key
                  }`;
                  return (
                    <FilterComponent
                      key={key}
                      idPrefix="SearchFiltersSecondary"
                      config={filterConfig}
                      listingCategories={listingCategories}
                      marketplaceCurrency={marketplaceCurrency}
                      urlQueryParams={validQueryParams}
                      initialValues={initialValues(props, currentQueryParams)}
                      getHandleChangedValueFn={getHandleChangedValueFn}
                      intl={intl}
                      showAsPopup={false}
                    />
                  );
                })}
              </SearchFiltersSecondary>
            </div>
          ) : (
            <div
              className={classNames(css.listingsForMapVariant, {
                [css.newSearchInProgress]: !(listingsAreLoaded || searchListingsError),
              })}
            >
              {searchListingsError ? (
                <H3 className={css.error}>
                  <FormattedMessage id="SearchPage.searchError" />
                </H3>
              ) : null}
              {!isValidDatesFilter ? (
                <H5>
                  <FormattedMessage id="SearchPage.invalidDatesFilter" />
                </H5>
              ) : null}
              <SearchResultsPanel
                className={css.searchListingsPanel}
                listings={listings}
                pagination={listingsAreLoaded ? pagination : null}
                search={parse(location.search)}
                setActiveListing={onActivateListing}
                isMapVariant
              />
            </div>
          )}
        </div>
        <ModalInMobile
          className={css.mapPanel}
          id="SearchPage.map"
          isModalOpenOnMobile={isSearchMapOpenOnMobile}
          onClose={() => setIsSearchMapOpenOnMobile(false)}
          showAsModalMaxWidth={MODAL_BREAKPOINT}
          onManageDisableScrolling={onManageDisableScrolling}
        >
          <div className={css.mapWrapper} data-testid="searchMapContainer">
            {shouldShowSearchMap ? (
              <SearchMap
                reusableContainerClassName={css.map}
                activeListingId={activeListingId}
                bounds={bounds}
                center={origin}
                isSearchMapOpenOnMobile={isSearchMapOpenOnMobile}
                location={location}
                listings={listings || []}
                onMapMoveEnd={onMapMoveEnd}
                onCloseAsModal={() => {
                  onManageDisableScrolling('SearchPage.map', false);
                }}
                messages={intl.messages}
              />
            ) : null}
          </div>
        </ModalInMobile>
      </div>
      <FooterComponent />
    </Page>
  );
};

SearchPageComponent.defaultProps = {
  listings: [],
  pagination: null,
  searchListingsError: null,
  searchParams: {},
  activeListingId: null,
};

SearchPageComponent.propTypes = {
  listings: array,
  onActivateListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  pagination: propTypes.pagination,
  scrollingDisabled: bool.isRequired,
  searchInProgress: bool.isRequired,
  searchListingsError: propTypes.error,
  searchParams: object,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  return (
    <SearchPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
    activeListingId,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onActivateListing: listingId => dispatch(setActiveListing(listingId)),
});

const SearchPage = compose(connect(mapStateToProps, mapDispatchToProps))(EnhancedSearchPage);

export default SearchPage;
